import { addStyles } from 'ethcss'
import { IStyles, mediaCSS } from 'theme'
import { color } from 'theme/tokens/color'

const styles: IStyles = {
    wrapper: {
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: color.bgWhitePrimary,
        borderRadius: 16,
    },
    scrollArea: {
        height: '100%',
        minHeight: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
    },
    table: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 760,
        marginTop: 5,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '40px minmax(280px, 1fr) minmax(180px, 0.65fr) 120px 40px',
        alignItems: 'center',
        minHeight: 68,
        borderBottom: `1px solid ${color.borderDarkPrimary}`,
        '&:hover [data-content-delete-button="true"]': {
            opacity: 1,
        },
    },
    header: {
        flexShrink: 0,
        minHeight: 44,
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: '8px 8px 0 0',
        backgroundColor: color.bgWhiteSecondary,
        color: color.blue,
        fontSize: 13,
    },
    rowsScrollArea: {
        flex: '1 1 auto',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
    },
    cell: {
        minWidth: 0,
        padding: '8px 12px',
    },
    checkboxCell: {
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
    },
    contentCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    thumbnail: {
        flex: '0 0 52px',
        width: 52,
        height: 52,
        borderRadius: 6,
        objectFit: 'cover',
        backgroundColor: color.bgWhiteTertiary,
    },
    videoThumbnail: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: color.bgDarkPrimary,
    },
    contentMeta: {
        minWidth: 0,
    },
    contentName: {
        overflow: 'hidden',
        color: color.fontDarkUltraHigh,
        fontSize: 14,
        lineHeight: '18px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    preview: {
        display: 'inline-block',
        marginTop: 4,
        border: 0,
        padding: 0,
        color: color.blue,
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 13,
    },
    status: {
        fontSize: 13,
    },
    statusPending: {
        color: color.warning,
    },
    statusApproved: {
        color: color.success,
    },
    statusRejected: {
        color: color.critical,
    },
    actionButton: {
        minWidth: 82,
        padding: '0 10px',
    },
    deleteButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        '&:disabled': {
            opacity: 0.35,
            cursor: 'default',
        },
    },
    rowDeleteButton: {
        opacity: 0,
        transition: 'opacity 150ms ease',
        '&:focus-visible': {
            opacity: 1,
        },
        '@media (hover: none)': {
            opacity: 1,
        },
    },
    pagination: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 48,
        color: color.fontDarkHigh,
        fontSize: 13,
    },
    paginationLink: {
        color: color.blue,
    },
    empty: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        height: '100%',
        minHeight: 340,
        textAlign: 'center',
    },
    emptyIcon: {
        display: 'block',
        width: 184,
        height: 117,
        marginBottom: 20,
    },
    emptyTitle: {
        marginBottom: 8,
        color: color.fontDarkUltraHigh,
        fontSize: 18,
        fontWeight: 500,
    },
    emptyText: {
        color: color.fontDarkHigh,
        fontSize: 13,
    },
    sideContent: {
        height: 'calc(100% - 152px)',
        overflowY: 'auto',
        padding: '24px 32px',
        [mediaCSS.tab]: {
            padding: 20,
        },
    },
    modalFile: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 24,
    },
    modalThumbnail: {
        width: 52,
        height: 52,
        borderRadius: 6,
        objectFit: 'cover',
    },
    question: {
        marginBottom: 4,
        color: color.fontDarkUltraHigh,
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '22px',
    },
    description: {
        marginBottom: 20,
        color: color.fontDarkHigh,
        fontSize: 12,
        lineHeight: '16px',
    },
    radio: {
        marginBottom: 14,
    },
    commentLabel: {
        display: 'block',
        margin: '24px 0 8px',
        color: color.fontDarkUltraHigh,
        fontSize: 13,
    },
    textarea: {
        display: 'block',
        width: '100%',
        minHeight: 92,
        resize: 'vertical',
        border: `1px solid transparent`,
        borderRadius: 8,
        padding: 12,
        color: color.fontDarkUltraHigh,
        backgroundColor: color.bgWhiteSecondary,
        font: 'inherit',
        outline: 'none',
        '&:focus': {
            borderColor: color.blue,
        },
    },
    counter: {
        marginTop: 6,
        color: color.fontDarkHigh,
        fontSize: 12,
        textAlign: 'right',
    },
    notice: {
        display: 'flex',
        gap: 12,
        marginTop: 24,
        border: `1px solid ${color.info200}`,
        borderRadius: 8,
        padding: 14,
        backgroundColor: color.info100,
    },
    noticeTitle: {
        marginBottom: 4,
        fontSize: 14,
        fontWeight: 500,
    },
    noticeText: {
        color: color.fontDarkHigh,
        fontSize: 12,
        lineHeight: '16px',
    },
    dialogOverlay: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
    dialog: {
        position: 'relative',
        width: 520,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 16,
        backgroundColor: color.bgWhitePrimary,
        overflow: 'hidden',
    },
    dialogClose: {
        position: 'absolute',
        top: 18,
        right: 18,
        zIndex: 1,
        border: 0,
        padding: 0,
        background: 'transparent',
        cursor: 'pointer',
    },
    dialogBody: {
        padding: 24,
    },
    dialogTitle: {
        marginBottom: 20,
        paddingRight: 28,
        color: color.fontDarkUltraHigh,
        fontSize: 22,
        fontWeight: 500,
    },
    detailStatus: {
        margin: '8px 0 20px',
        fontSize: 14,
    },
    detailComment: {
        borderRadius: 8,
        padding: 12,
        backgroundColor: color.bgWhiteSecondary,
        color: color.fontDarkUltraHigh,
        fontSize: 13,
        lineHeight: '18px',
    },
    dialogFooter: {
        display: 'flex',
        gap: 8,
        borderTop: `1px solid ${color.borderDarkPrimary}`,
        padding: 16,
    },
    dangerButton: {
        border: 0,
        color: color.critical,
        backgroundColor: color.critical100,
    },
}

addStyles(styles)

export default styles
